import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../common/context/AuthContext';
import { Lock, Mail, Phone, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { API_URL } from '../../../api/config';

const SetupAdminPin = () => {
  const navigate = useNavigate();
  const { userToken: token } = useAuth();

  const [step, setStep] = useState(1); // 1: Email+Phone, 2: OTP, 3: PIN, 4: Confirm PIN
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const otpRefs = useRef([]);
  const pinRefs = useRef([]);
  const confirmPinRefs = useRef([]);

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  // ─── Step 1: Send OTP ────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (phoneNumber && !/^\d{10}$/.test(phoneNumber)) {
      toast.error('Phone number must be 10 digits');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/pin-settings/send-otp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          role: 'admin',
          ...(phoneNumber && { phoneNumber }),
        }),
      });
      const data = await response.json();

      if (response.ok) {
        toast.success('OTP sent to your email');
        setStep(2);
        setCountdown(60);
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 2: Verify OTP ──────────────────────────────────────────
  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) { toast.error('Please enter the complete 6-digit OTP'); return; }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/pin-settings/verify-otp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp: otpString, role: 'admin' }),
      });
      const data = await response.json();

      if (response.ok) {
        toast.success('OTP verified');
        setStep(3);
      } else {
        toast.error(data.message || 'Invalid OTP');
      }
    } catch {
      toast.error('Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  // ─── Steps 3 & 4: Set PIN ────────────────────────────────────────
  const handleSetPin = async () => {
    const pinString = pin.join('');
    const confirmPinString = confirmPin.join('');

    if (pinString.length !== 4) { toast.error('Please enter a 4-digit PIN'); return; }

    if (step === 3) { setStep(4); return; }

    if (pinString !== confirmPinString) { toast.error('PINs do not match'); return; }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/pin-settings/set-admin-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pin: pinString,
          email: email.trim().toLowerCase(),
          ...(phoneNumber && { phoneNumber }),
        }),
      });
      const data = await response.json();

      if (response.ok) {
        toast.success('Admin PIN set successfully');
        setTimeout(() => navigate('/pin-protection'), 1500);
      } else {
        toast.error(data.message || 'Failed to set PIN');
      }
    } catch {
      toast.error('Failed to set PIN');
    } finally {
      setLoading(false);
    }
  };

  // ─── Input helpers ───────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    if (value.length > 1 || (value && !/^\d+$/.test(value))) return;
    const next = [...otp]; next[index] = value; setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handlePinChange = (index, value, isPin = true) => {
    if (value.length > 1 || (value && !/^\d+$/.test(value))) return;
    if (isPin) {
      const next = [...pin]; next[index] = value; setPin(next);
      if (value && index < 3) pinRefs.current[index + 1]?.focus();
    } else {
      const next = [...confirmPin]; next[index] = value; setConfirmPin(next);
      if (value && index < 3) confirmPinRefs.current[index + 1]?.focus();
    }
  };
  const handlePinKeyDown = (index, e, isPin = true) => {
    const cur = isPin ? pin : confirmPin;
    const refs = isPin ? pinRefs : confirmPinRefs;
    if (e.key === 'Backspace' && !cur[index] && index > 0) refs.current[index - 1]?.focus();
  };

  const STEPS = ['Email', 'OTP', 'PIN', 'Confirm'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-8 text-white">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
              <Lock size={32} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Set Admin PIN</h1>
            <p className="text-purple-100 text-sm font-medium">
              {step === 1 && 'Enter your email to receive a verification code'}
              {step === 2 && 'Enter the 6-digit code sent to your email'}
              {step === 3 && 'Create a 4-digit PIN for admin access'}
              {step === 4 && 'Confirm your PIN'}
            </p>
          </div>

          {/* Progress */}
          <div className="px-8 py-6 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center justify-between">
              {STEPS.map((label, i) => {
                const stepNum = i + 1;
                return (
                  <React.Fragment key={stepNum}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                        step >= stepNum ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-400'
                      }`}>
                        {step > stepNum ? <CheckCircle2 size={20} /> : stepNum}
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-wider">{label}</span>
                    </div>
                    {stepNum < 4 && (
                      <div className={`flex-1 h-0.5 mx-2 transition-all ${step > stepNum ? 'bg-purple-600' : 'bg-slate-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">

            {/* ── Step 1: Email + optional phone ── */}
            {step === 1 && (
              <div className="space-y-5">
                {/* Email (required) */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-xl text-base font-semibold text-slate-900 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                    />
                  </div>
                  <p className="text-xs text-slate-400">OTP will be sent to this email</p>
                </div>

                {/* Phone (optional) */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Mobile Number <span className="text-slate-400 font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="tel"
                      maxLength={10}
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder="10-digit number (optional)"
                      className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-xl text-base font-semibold text-slate-900 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Saved for reference only, not used for OTP</p>
                </div>

                <button
                  onClick={handleSendOtp}
                  disabled={loading || !isValidEmail(email)}
                  className="w-full py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP via Email'}
                </button>
              </div>
            )}

            {/* ── Step 2: OTP ── */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Enter OTP
                  </label>
                  <div className="flex gap-3 justify-center">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-300 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500 font-medium">Sent to {email}</span>
                    {countdown > 0 ? (
                      <span className="text-purple-600 font-bold">Resend in {countdown}s</span>
                    ) : (
                      <button onClick={handleSendOtp} className="text-purple-600 font-bold hover:text-purple-700">
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.join('').length !== 6}
                  className="w-full py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            )}

            {/* ── Step 3: Enter PIN ── */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Create 4-Digit PIN
                  </label>
                  <div className="flex gap-3 justify-center">
                    {pin.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (pinRefs.current[index] = el)}
                        type="password"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(index, e.target.value, true)}
                        onKeyDown={(e) => handlePinKeyDown(index, e, true)}
                        className="w-14 h-16 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-center text-slate-500 font-medium">Choose a PIN you'll remember</p>
                </div>
                <button
                  onClick={handleSetPin}
                  disabled={pin.join('').length !== 4}
                  className="w-full py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            )}

            {/* ── Step 4: Confirm PIN ── */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Confirm Your PIN
                  </label>
                  <div className="flex gap-3 justify-center">
                    {confirmPin.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (confirmPinRefs.current[index] = el)}
                        type="password"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(index, e.target.value, false)}
                        onKeyDown={(e) => handlePinKeyDown(index, e, false)}
                        className="w-14 h-16 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-center text-slate-500 font-medium">Re-enter your PIN to confirm</p>
                </div>
                <button
                  onClick={handleSetPin}
                  disabled={loading || confirmPin.join('').length !== 4}
                  className="w-full py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                >
                  {loading ? 'Setting PIN...' : 'Set Admin PIN'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Notice */}
        <div className="mt-6 bg-purple-50 border border-purple-100 rounded-xl p-4">
          <p className="text-xs text-purple-900 font-medium leading-relaxed">
            🔒 OTP is sent to your email. Your PIN is encrypted and stored securely.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupAdminPin;